/**
 * Tiny shared UI primitives. Plain CSS classes from styles.css — no UI library.
 */
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import type { BuildStatus, SubscriptionStatus } from '@appbuilder/shared';

// ─── Button ──────────────────────────────────────────────────────────────────

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  loading = false,
  disabled,
  children,
  className,
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      className={`btn btn-${variant}${className ? ` ${className}` : ''}`}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? 'Working…' : children}
    </button>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────────

export function Card({
  title,
  description,
  actions,
  children,
}: {
  title?: ReactNode;
  /** Plain-language "what this section does" — shown as a subtitle so the UI explains itself. */
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          <div className="card-head-text">
            {title && <h2 className="card-title">{title}</h2>}
            {description && <p className="card-desc">{description}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </header>
      )}
      <div className="card-body">{children}</div>
    </section>
  );
}

// ─── Field (label + input/select/textarea) ──────────────────────────────────

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
}

export function Field({ label, hint, error, id, ...rest }: FieldProps): JSX.Element {
  const fieldId = id ?? `f-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <input id={fieldId} className={`field-input${error ? ' field-input-error' : ''}`} {...rest} />
      {hint && !error && <span className="field-hint">{hint}</span>}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: readonly { value: string; label: string }[];
}

export function SelectField({ label, options, id, ...rest }: SelectFieldProps): JSX.Element {
  const fieldId = id ?? `s-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <select id={fieldId} className="field-input" {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string;
}

export function TextAreaField({ label, hint, id, ...rest }: TextAreaFieldProps): JSX.Element {
  const fieldId = id ?? `t-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field">
      <label htmlFor={fieldId} className="field-label">
        {label}
      </label>
      <textarea id={fieldId} className="field-input field-textarea" {...rest} />
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

// ─── Toggle (labeled checkbox) ───────────────────────────────────────────────

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  disabled?: boolean;
  id?: string;
}

export function Toggle({ label, checked, onChange, hint, disabled, id }: ToggleProps): JSX.Element {
  const fieldId = id ?? `tg-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div className="field field-toggle">
      <label htmlFor={fieldId} className="toggle">
        <input
          id={fieldId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="field-label">{label}</span>
      </label>
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

// ─── StatusBadge ─────────────────────────────────────────────────────────────

type BadgeTone = 'neutral' | 'info' | 'success' | 'warn' | 'danger';

function buildTone(status: BuildStatus): BadgeTone {
  switch (status) {
    case 'succeeded':
      return 'success';
    case 'failed':
    case 'canceled':
      return 'danger';
    case 'queued':
      return 'neutral';
    default:
      return 'info'; // claimed | building | uploading
  }
}

function subscriptionTone(status: SubscriptionStatus): BadgeTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'grace':
      return 'warn';
    case 'pending':
      return 'info';
    default:
      return 'danger'; // on_hold | canceled | expired
  }
}

export function StatusBadge({
  status,
  kind,
}: {
  status: BuildStatus | SubscriptionStatus;
  kind: 'build' | 'subscription';
}): JSX.Element {
  const tone =
    kind === 'build'
      ? buildTone(status as BuildStatus)
      : subscriptionTone(status as SubscriptionStatus);
  return <span className={`badge badge-${tone}`}>{status}</span>;
}

// ─── Banner (errors / notices) ───────────────────────────────────────────────

export function Banner({
  tone = 'danger',
  children,
}: {
  tone?: 'danger' | 'info' | 'success';
  children: ReactNode;
}): JSX.Element {
  return <div className={`banner banner-${tone}`}>{children}</div>;
}
